import api from '@/services/apiService';

// Kota interface from API response
export interface Kota {
  kode: string;
  nama: string;
  nama_lengkap: string;
  level: string;
}

// Kota option for select components
export interface KotaOption {
  value: string;
  label: string;
  nama: string;
  namaLengkap: string;
}

// API response interface
interface WilayahApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

class WilayahService {
  /**
   * Search kota/kabupaten by name
   * @param query - Search query (minimum 3 characters recommended)
   * @returns Promise<KotaOption[]> - Array of kota options for select
   */
  async searchKota(query: string): Promise<KotaOption[]> {
    try {
      const response = await api.get<WilayahApiResponse<Kota[]>>(
        `/wilayah/kota/search?q=${encodeURIComponent(query)}`
      );

      if (response.data?.success && response.data?.data) {
        return response.data.data.map((item) => ({
          value: item.kode,
          label: item.nama_lengkap,
          nama: item.nama,
          namaLengkap: item.nama_lengkap,
        }));
      }

      return [];
    } catch (error) {
      console.error('Failed to search kota:', error);
      return [];
    }
  }

  /**
   * Get kota/kabupaten by kode
   * @param kode - Kode wilayah
   * @returns Promise<KotaOption | null> - Single kota option or null
   */
  async getKotaByKode(kode: string): Promise<KotaOption | null> {
    try {
      const response = await api.get<WilayahApiResponse<Kota>>(
        `/wilayah/kota/${encodeURIComponent(kode)}`
      );

      if (response.data?.success && response.data?.data) {
        const item = response.data.data;
        return {
          value: item.kode,
          label: item.nama_lengkap,
          nama: item.nama,
          namaLengkap: item.nama_lengkap,
        };
      }

      return null;
    } catch (error) {
      console.error('Failed to get kota by kode:', error);
      return null;
    }
  }

  /**
   * Get any wilayah by kode
   * @param kode - Kode wilayah
   * @returns Promise<KotaOption | null> - Single kota option or null
   */
  async getWilayahByKode(kode: string): Promise<KotaOption | null> {
    try {
      const response = await api.get<WilayahApiResponse<Kota>>(
        `/wilayah/${encodeURIComponent(kode)}`
      );

      if (response.data?.success && response.data?.data) {
        const item = response.data.data;
        return {
          value: item.kode,
          label: item.nama_lengkap,
          nama: item.nama,
          namaLengkap: item.nama_lengkap,
        };
      }

      return null;
    } catch (error) {
      console.error('Failed to get wilayah by kode:', error);
      return null;
    }
  }
}

// Export singleton instance
export const wilayahService = new WilayahService();
export default wilayahService;
